import React, { useRef, useState } from 'react';
import { Download, Copy, Check, MapPin, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

const DEFAULTS = {
  pillText: '10-Week Project · 2026',
  tagline: 'The 10-Week NZ',
  headlineLine1: 'Heifer Weigh',
  headlineLine2: 'Project',
  subheadline: 'From Kaitaia to Bluff — on the ground with dairy farmers & TOP inspection programs.',
  statWeeks: '10',
  statHeifers: '28,000',
  statFarms: '250',
  ctaUrl: 'measuredlivestock.com',
};

/**
 * FB Ad — The 10-Week NZ Heifer Weigh Project.
 * Design: Bold Stat-Hero (dark rural background, big numbers, Kaitaia → Bluff route).
 *
 * Editable fields via `overrides` prop (all optional — fall back to DEFAULTS).
 *
 * Formats:
 *   1) Portrait  1080 × 1350  (FB / IG feed)
 *   2) Landscape 1200 × 630   (FB feed + link previews)
 */
const HeiferProjectAd = ({ overrides = {} }) => {
  // Merge overrides into defaults, but treat empty strings as "use default"
  const cfg = Object.fromEntries(
    Object.entries(DEFAULTS).map(([k, v]) => {
      const o = overrides[k];
      return [k, (o !== undefined && o !== null && String(o).trim() !== '') ? o : v];
    })
  );
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0a1f13',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `THE 10-WEEK NZ HEIFER WEIGH PROJECT 🚜

Over the next 10 weeks, the Measured Livestock team will be out on the ground right across New Zealand — from Kaitaia to Bluff.

We're working alongside dairy farmers and TOP inspection programs to weigh and capture data on more than 28,000 dairy heifers across 250 farms.

It's a solid run of yards, early starts, long days, and a lot of good people getting stuck into the work.

Every animal we weigh is captured live in the yards — keeping things moving quickly, accurately, and without the paperwork slowing anything down.

📍 WHAT WE'LL BE SHARING
•  The farms we're visiting
•  The people behind the stock
•  Life on the road
•  What it actually takes to get the job done

Follow along and see the mahi in action.
👉 measuredlivestock.com

#NZDairy #HeiferWeighing #DairyNZ #NZFarming #MeasuredLivestock #TOP #AnimalIDVerified`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const heroImg = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1600&q=80';

  // A single stat block — used inline on both formats.
  // Sizes standardised so 3 blocks fit within the 968px content width without overflow.
  const StatBlock = ({ number, label, sub }) => (
    <div style={{
      textAlign: 'center', flex: 1, minWidth: 0, overflow: 'hidden', padding: '0 4px',
    }}>
      <div style={{
        fontSize: 76,
        lineHeight: 1,
        fontWeight: 900,
        color: '#ff8533',
        letterSpacing: -2,
        textShadow: '0 6px 24px rgba(0,0,0,0.5)',
        whiteSpace: 'nowrap',
      }}>{number}</div>
      <div style={{
        fontSize: 18,
        fontWeight: 800,
        letterSpacing: 3,
        textTransform: 'uppercase',
        marginTop: 10,
        color: 'white',
      }}>{label}</div>
      {sub && (
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4, letterSpacing: 1 }}>
          {sub}
        </div>
      )}
    </div>
  );

  return (
    <section className="bg-gradient-to-br from-[#0a1f13] via-[#0f2818] to-[#0a1f13] py-12" data-testid="heifer-project-ad-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            New · National Campaign · 10-Week Project
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <MapPin className="w-9 h-9 text-[#ff6b00]" />
            The 10-Week NZ Heifer Weigh Project
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Kaitaia to Bluff · 28,000 dairy heifers · 250 farms · captured live in the yards.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* ================= PORTRAIT 1080 × 1350 ================= */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-heifer-project-1080x1350.png')}
                disabled={downloading === 'measuredlivestock-heifer-project-1080x1350.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-heifer-project-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-heifer-project-1080x1350.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080,
                  height: 1350,
                  marginRight: -1080 * 0.58,
                  marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080,
                    height: 1350,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(180deg, rgba(10,31,19,0.94) 0%, rgba(10,31,19,0.82) 40%, rgba(10,31,19,0.96) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '56px',
                    boxSizing: 'border-box',
                  }}
                >
                  {/* Top row: Brand + Duration pill */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                      <span style={{ fontSize: 14, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '10px 22px', borderRadius: 999,
                      fontSize: 14, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
                    }}>
                      {cfg.pillText}
                    </div>
                  </div>

                  {/* Headline block */}
                  <div style={{ textAlign: 'center', marginTop: 44 }}>
                    <div style={{
                      fontSize: 22, fontWeight: 700, opacity: 0.85, letterSpacing: 6,
                      textTransform: 'uppercase', marginBottom: 16,
                    }}>
                      {cfg.tagline}
                    </div>
                    <h1 style={{
                      fontSize: 96, lineHeight: 0.98, fontWeight: 900, margin: 0,
                      letterSpacing: -3, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      {cfg.headlineLine1}
                    </h1>
                    <h1 style={{
                      fontSize: 96, lineHeight: 0.98, fontWeight: 900, margin: '4px 0 20px',
                      color: '#ff8533', letterSpacing: -3,
                    }}>
                      {cfg.headlineLine2}
                    </h1>
                    <div style={{
                      fontSize: 26, opacity: 0.94, fontWeight: 500, lineHeight: 1.35,
                      maxWidth: 820, margin: '0 auto',
                    }}>
                      {cfg.subheadline}
                    </div>
                  </div>

                  {/* Kaitaia → Bluff route line */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, marginTop: 40,
                  }}>
                    <div style={{
                      background: '#ff6b00', width: 14, height: 14, borderRadius: '50%',
                      boxShadow: '0 0 0 4px rgba(255,107,0,0.25)',
                    }} />
                    <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', flexShrink: 0 }}>Kaitaia</div>
                    <div style={{
                      flex: 1, height: 2, background: 'linear-gradient(90deg, #ff6b00, rgba(255,255,255,0.35), #ff6b00)',
                    }} />
                    <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', flexShrink: 0 }}>Bluff</div>
                    <div style={{
                      background: '#ff6b00', width: 14, height: 14, borderRadius: '50%',
                      boxShadow: '0 0 0 4px rgba(255,107,0,0.25)',
                    }} />
                  </div>

                  {/* Stats grid */}
                  <div style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1.5px solid rgba(255,107,0,0.35)',
                    borderRadius: 16, padding: '30px 20px',
                    display: 'flex', gap: 12, marginTop: 28,
                  }}>
                    <StatBlock number={cfg.statWeeks} label="Weeks" sub="Solid run of yards" />
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)' }} />
                    <StatBlock number={cfg.statHeifers} label="Heifers" sub="Dairy · Live in yards" />
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)' }} />
                    <StatBlock number={cfg.statFarms} label="Farms" sub="Right across NZ" />
                  </div>

                  {/* What we'll be sharing */}
                  <div style={{ marginTop: 28 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 12, textAlign: 'center',
                    }}>
                      📍  What we'll be sharing
                    </div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10,
                    }}>
                      {[
                        'The farms we\'re visiting',
                        'The people behind the stock',
                        'Life on the road',
                        'What it takes to get the job done',
                      ].map((line) => (
                        <div key={line} style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.14)',
                          borderRadius: 10,
                          padding: '12px 14px',
                          display: 'flex', alignItems: 'center', gap: 10,
                          fontSize: 18, fontWeight: 600,
                        }}>
                          <span style={{ color: '#ff8533', fontWeight: 900 }}>✓</span>
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA — pinned to bottom via marginTop: auto */}
                  <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: 24 }}>
                    <div style={{
                      fontSize: 16, fontWeight: 700, color: '#ff8533',
                      letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10,
                    }}>
                      Follow the journey
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '20px 0', borderRadius: 10,
                      fontSize: 30, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      {cfg.ctaUrl}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ================= LANDSCAPE 1200 × 630 ================= */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-heifer-project-1200x630.png')}
                disabled={downloading === 'measuredlivestock-heifer-project-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-heifer-project-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-heifer-project-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: 1200,
                  height: 630,
                  marginRight: -1200 * 0.45,
                  marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(110deg, rgba(10,31,19,0.96) 0%, rgba(10,31,19,0.88) 55%, rgba(10,31,19,0.55) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  {/* Brand */}
                  <div style={{ position: 'absolute', top: 36, left: 56, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 6, height: 32, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, textTransform: 'uppercase', opacity: 0.92 }}>
                      Measured Livestock
                    </span>
                  </div>

                  {/* Duration pill */}
                  <div style={{
                    position: 'absolute', top: 36, right: 56,
                    background: '#ff6b00', color: 'white', padding: '8px 20px', borderRadius: 999,
                    fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
                  }}>
                    {cfg.pillText}
                  </div>

                  {/* Left text */}
                  <div style={{ position: 'absolute', top: 108, left: 56, right: 460 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, opacity: 0.88, marginBottom: 6, letterSpacing: 4, textTransform: 'uppercase' }}>
                      {cfg.tagline}
                    </div>
                    <h1 style={{
                      fontSize: 62, lineHeight: 1.0, fontWeight: 900, margin: 0,
                      letterSpacing: -1.4,
                    }}>
                      {cfg.headlineLine1}
                    </h1>
                    <h1 style={{
                      fontSize: 62, lineHeight: 1.0, fontWeight: 900, margin: '4px 0 14px',
                      color: '#ff8533', letterSpacing: -1.4,
                    }}>
                      {cfg.headlineLine2}
                    </h1>
                    <div style={{ fontSize: 17, fontWeight: 400, opacity: 0.94, lineHeight: 1.5, maxWidth: 620 }}>
                      {cfg.subheadline}
                    </div>

                    {/* Route */}
                    <div style={{
                      marginTop: 22, display: 'flex', alignItems: 'center', gap: 10, maxWidth: 560,
                    }}>
                      <div style={{ background: '#ff6b00', width: 12, height: 12, borderRadius: '50%' }} />
                      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>Kaitaia</div>
                      <div style={{ flex: 1, height: 2, background: 'linear-gradient(90deg, #ff6b00, rgba(255,255,255,0.35), #ff6b00)' }} />
                      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>Bluff</div>
                      <div style={{ background: '#ff6b00', width: 12, height: 12, borderRadius: '50%' }} />
                    </div>
                  </div>

                  {/* Right — stats card */}
                  <div style={{
                    position: 'absolute', top: 108, right: 56, width: 380,
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px solid rgba(255,107,0,0.55)',
                    borderRadius: 14, padding: '22px 22px',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#ff8533', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12, textAlign: 'center' }}>
                      By the numbers
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div>
                        <div style={{ fontSize: 44, fontWeight: 900, color: '#ff8533', lineHeight: 1 }}>{cfg.statWeeks}</div>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85 }}>Weeks</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 44, fontWeight: 900, color: '#ff8533', lineHeight: 1 }}>28k</div>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85 }}>Heifers</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 44, fontWeight: 900, color: '#ff8533', lineHeight: 1 }}>{cfg.statFarms}</div>
                        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85 }}>Farms</div>
                      </div>
                    </div>

                    <div style={{
                      borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 12,
                      fontSize: 12, opacity: 0.9, lineHeight: 1.5,
                    }}>
                      Yards captured live · Traceable · Every animal
                      <span style={{ color: '#ff8533', fontWeight: 800 }}> Animal ID Verified</span>.
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{
                    position: 'absolute', bottom: 36, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24,
                  }}>
                    <div>
                      <div style={{ fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                        Follow the journey
                      </div>
                      <div style={{ fontSize: 30, fontWeight: 800 }}>{cfg.ctaUrl}</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '18px 32px', borderRadius: 8,
                      fontSize: 20, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.4)',
                    }}>
                      Follow Along →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-heifer-project-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Suggested use:</strong> National launch post + weekly follow-ups. Ideal audience:
            <em> Dairy farmers, DairyNZ, LIC, CRV, Federated Farmers, NAIT users across NZ</em>.
            Pair with a Reel of the team in the yards for higher engagement.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeiferProjectAd;
